<script setup lang="ts">
import type { DocumentSignFollower } from '../../../../Types/document'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import { computed, ref } from 'vue'
import useModal from '../../../../Composables/useModal'
import FormCreator from '../UI/Form/FormCreator.vue'
import { useI18n } from 'vue-i18n'
import type { FormCreatorType } from '../../../../Types/form'
import useLoading from '../../../../Composables/useLoading'
import LazyContent from '../UI/Loading/LazyContent.vue'

interface Props {
  documentSignId: Number
  documentSignFollower?: DocumentSignFollower
}
const props = defineProps<Props>()
const { t } = useI18n()

const emit = defineEmits<{
  save: [{ follower: DocumentSignFollower }]
}>()

const { modal, modalId, open, close } = useModal({ prefix: 'modal-follower-' })
const modalTitle = computed(() => {
  return t('contract.document.documentSign.follower.' + (isUpdate.value ? 'modalEditTitle' : 'modalAddTitle'))
})

const isUpdate = computed(() => {
  return props.documentSignFollower?.id !== undefined
})

const params = computed(() => {
  if (!props.documentSignFollower) {
    return { _z: 1 }
  }
  return { followerId: props.documentSignFollower.id }
})
const endpoint = computed(() => {
  return `document-signs/${props.documentSignId}/followers`
})
const method = computed(() => {
  return isUpdate.value ? 'patch' : 'post'
})
const specificEndpoint = computed(() => {
  return props.documentSignFollower?.id?.toString() ?? ''
})

const form = ref<FormCreatorType | null>(null)
const formResetKey = ref<number>(0)
const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

function resetFormState(): void {
  form.value = null
  setLoading(true)
  formResetKey.value += 1
}
async function submit() {
  if (!form.value) {
    return
  }
  sending.value = true
  try {
    await form.value.methods()
  } finally {
    sending.value = false
  }
}

function submitted(follower: DocumentSignFollower) {
  emit('save', { follower: follower })
  resetFormState()
  close()
}
</script>

<template>
  <div>
    <ButtonAll v-if="isUpdate" type="add" margin="no" icon="modify" @click="open" />
    <ButtonAll
      v-else
      type="outline_info"
      margin="no"
      label="contract.document.documentSign.follower.add"
      @click="open"
    />
    <ModalDialog :id="modalId" ref="modal" :title="modalTitle" :e2e-modal="modalId">
      <template #body>
        <FormCreator
          :key="formResetKey"
          ref="formDocumentSignFollower"
          class="d-none"
          :endpoint="endpoint"
          :params="params"
          :method="method"
          class-to-add="d-none"
          :specific-endpoint="specificEndpoint"
          @form-return="submitted"
          @update-data="onFormLoad"
          @is-loading="setLoading"
        />
        <LazyContent :loading="loading" :class="{ 'p-2': loading }">
          <div v-if="form" class="row">
            <FieldCreator :form-data="form" form-ref="formDocumentSignFollower" hidden name="id" />
            <div class="col-12">
              <FieldCreator
                :form-data="form"
                form-ref="formDocumentSignFollower"
                label-top
                name="name"
                domain="contract.document.documentSign.follower"
              />
            </div>
            <div class="col-12">
              <FieldCreator
                :form-data="form"
                form-ref="formDocumentSignFollower"
                label-top
                name="email"
                domain="contract.document.documentSign.follower"
                autocomplete="off"
                data-bwignore="true"
              />
            </div>
          </div>
        </LazyContent>
      </template>
      <template v-if="!loading" #footer>
        <ButtonAll margin="no" type="default" label="main.close" e2e-button="close" @click="close" />
        <ButtonAll
          margin="no"
          type="validation"
          label="contract.document.documentSign.follower.validate"
          :loading="sending"
          @click="submit"
        />
      </template>
    </ModalDialog>
  </div>
</template>
